import { useState, useCallback, useMemo } from "react";
import { getIncomeSummary } from "../../api/finances/incomeAPI";
import {
  formatDateToMonthYear,
  getPeriodDateRange,
} from "../../utils/date/DateUtils";

export function useIncomeUsage(
  initialPeriod = "3M",
  initialCustomRange = null,
) {
  const [appliedPeriod, setAppliedPeriod] = useState(initialPeriod);
  const [appliedCustomDateRange, setAppliedCustomDateRange] =
    useState(initialCustomRange);
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [incomeData, setIncomeData] = useState({
    summary: { total: "0.00" },
    chart: [],
    candidates: [],
    transactions: [],
  });

  const fetchIncome = useCallback(async () => {
    setIsLoading(true);
    setIsPending(true);
    setError("");

    try {
      let params = {};
      if (appliedPeriod === "custom" && appliedCustomDateRange) {
        params = {
          start_date: appliedCustomDateRange.start_date,
          end_date: appliedCustomDateRange.end_date,
        };
      } else {
        params = getPeriodDateRange(appliedPeriod);
      }

      const data = await getIncomeSummary(params);
      setIncomeData(
        data || {
          summary: { total: "0.00" },
          chart: [],
          candidates: [],
          transactions: [],
        },
      );
    } catch (err) {
      console.error("Failed to load income data:", err);
      setError("We could not load your income data. Please try again.");
    } finally {
      setIsLoading(false);
      setTimeout(() => setIsPending(false), 200);
    }
  }, [appliedPeriod, appliedCustomDateRange]);

  const formattedChartData = useMemo(() => {
    if (!incomeData.transactions || !Array.isArray(incomeData.transactions)) {
      return [];
    }

    const sortedTransactions = [...incomeData.transactions].sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    );

    return sortedTransactions.map((tx) => ({
      uuid: tx.uuid || tx.transaction_id,
      label: formatDateToMonthYear(tx.date),
      value: Math.abs(parseFloat(tx.amount || 0)),
      status: "Passed",
      rawTransaction: tx,
    }));
  }, [incomeData.transactions]);

  const handleBarClick = useCallback((barItem) => {
    if (barItem?.rawTransaction) {
      setSelectedTransaction(barItem.rawTransaction);
    }
  }, []);

  return {
    appliedPeriod,
    setAppliedPeriod,
    appliedCustomDateRange,
    setAppliedCustomDateRange,
    isLoading,
    isPending,
    error,
    incomeData,
    formattedChartData,
    selectedTransaction,
    setSelectedTransaction,
    fetchIncome,
    handleBarClick,
  };
}
